import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useSocketSubscription } from '../lib/socketManager';
import { permissionLabels } from '../pages/ManagerProfile';
import { useClient } from '../contexts/ClientContext';

interface ManagerPermissionsChangedModalProps {}

/**
 * Modal that shows when manager permissions are updated by admin
 * Forces browser refresh after showing the new permissions
 */
const ManagerPermissionsChangedModal: React.FC<ManagerPermissionsChangedModalProps> = () => {
  const { client } = useClient();
  const [isOpen, setIsOpen] = useState(false);
  const [newPermissions, setNewPermissions] = useState<string[]>([]);
  const [managerName, setManagerName] = useState<string>('');

  // Only show for managers (not admins)
  const isManager = client?.role === 'manager';

  // Listen for manager permissions updated event
  useSocketSubscription('manager:permissions-updated', (payload: any) => {
    // Only process if user is a manager and the event is for them
    if (!isManager) return;
    
    // Check if this event is for the current user
    const currentUserId = client?._id;
    const eventManagerId = payload?.managerId;
    
    if (currentUserId && eventManagerId && String(currentUserId) === String(eventManagerId)) {
      if (payload?.permissions && Array.isArray(payload.permissions)) {
        setNewPermissions(payload.permissions);
        setManagerName(payload.managerName || payload.fullName || '');
        setIsOpen(true);
      }
    }
  });

  const handleRefresh = () => {
    // Force full page reload to ensure all changes take effect
    window.location.reload();
  };

  // Get permission labels in Hebrew
  const getPermissionLabel = (permission: string): string => {
    return permissionLabels[permission] || permission;
  };

  // Group permissions by category for better display
  const groupedPermissions = React.useMemo(() => {
    const groups: Record<string, string[]> = {
      'לקוחות': [],
      'לידים': [],
      'משימות': [],
      'משתמשים': [],
      'סוכנים': [],
      'לקוחות פוטנציאליים': [],
      'הצעות מחיר': [],
      'ספקים': [],
      'מערכת': [],
      'אחר': [],
    };

    newPermissions.forEach((perm) => {
      const label = getPermissionLabel(perm);
      // Check proposals FIRST before other system permissions to avoid conflicts
      if (perm.includes('proposal')) {
        groups['הצעות מחיר'].push(label);
      } else if (perm.includes('vendor')) {
        groups['ספקים'].push(label);
      } else if (perm.includes('client') || perm.includes('payment')) {
        groups['לקוחות'].push(label);
      } else if (perm.includes('lead')) {
        groups['לידים'].push(label);
      } else if (perm.includes('task')) {
        groups['משימות'].push(label);
      } else if (perm.includes('manager')) {
        groups['משתמשים'].push(label);
      } else if (perm.includes('agent')) {
        groups['סוכנים'].push(label);
      } else if (perm.includes('potential')) {
        groups['לקוחות פוטנציאליים'].push(label);
      } else if (perm.includes('slot') || perm.includes('overview') || perm.includes('order') || perm.includes('session') || perm.includes('api') || perm.includes('failed') || perm.includes('unlock')) {
        groups['מערכת'].push(label);
      } else {
        groups['אחר'].push(label);
      }
    });

    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, perms]) => perms.length > 0)
    );
  }, [newPermissions]);

  // Don't render if not a manager
  if (!isManager) {
    return null;
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" dir="rtl" lang="he" onClose={() => {}} static>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl mx-4 transform overflow-hidden rounded-xl sm:rounded-2xl lg:rounded-3xl bg-white p-4 sm:p-6 lg:p-8 text-right align-middle shadow-2xl transition-all">
                <Dialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  עדכון הרשאות
                </Dialog.Title>

                <div className="mb-4 sm:mb-6">
                  <p className="text-base sm:text-lg text-gray-700 mb-3 sm:mb-4">
                    ההרשאות שלך עודכנו על ידי המנהל.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <p className="text-xs sm:text-sm text-yellow-800 font-semibold mb-1 sm:mb-2">
                      ⚠️ נדרש רענון דפדפן
                    </p>
                    <p className="text-xs sm:text-sm text-yellow-700 leading-relaxed">
                      כדי שהשינויים ייכנסו לתוקף ויופיעו במערכת, יש לרענן את הדפדפן. ללא רענון, השינויים לא יופיעו.
                    </p>
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">ההרשאות החדשות שלך:</h3>
                  <div className="max-h-64 sm:max-h-80 lg:max-h-96 overflow-y-auto space-y-3 sm:space-y-4 pr-1 sm:pr-2">
                    {Object.entries(groupedPermissions).map(([category, perms]) => (
                      <div key={category} className="border-b border-gray-200 pb-2 sm:pb-3 last:border-b-0">
                        <h4 className="font-semibold text-gray-800 mb-1.5 sm:mb-2 text-sm sm:text-base">{category}</h4>
                        <ul className="list-disc list-inside space-y-0.5 sm:space-y-1 text-xs sm:text-sm text-gray-700 pr-2">
                          {perms.map((perm, idx) => (
                            <li key={idx} className="break-words">{perm}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {newPermissions.length === 0 && (
                      <p className="text-gray-500 text-center py-3 sm:py-4 text-sm sm:text-base">אין הרשאות פעילות</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-center gap-3 sm:gap-4">
                  <button
                    onClick={handleRefresh}
                    className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg sm:rounded-xl font-semibold shadow-lg hover:bg-blue-700 active:bg-blue-800 transition-colors text-sm sm:text-base"
                  >
                    רענן דפדפן
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ManagerPermissionsChangedModal;

